import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Webhook URL - can be configured via environment variable or hardcoded
const WEBHOOK_URL = process.env.REACT_APP_WEBHOOK_URL || "";

export const LeadForm = ({ pageType, title = "Get $1500 Off & Schedule an Appointment" }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
  });
  
  // UTM parameters from URL
  const [utmParams, setUtmParams] = useState({
    utm_source: "",
    utm_medium: "",
    utm_campaign: "",
  });

  useEffect(() => {
    // Capture UTM parameters from URL
    setUtmParams({
      utm_source: searchParams.get("utm_source") || "",
      utm_medium: searchParams.get("utm_medium") || "",
      utm_campaign: searchParams.get("utm_campaign") || "",
    });
  }, [searchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const payload = {
        ...formData,
        page_type: pageType,
        service: pageType, // Hidden field for service/landing page
        ...utmParams,
        submitted_at: new Date().toISOString(),
        page_url: window.location.href,
      };

      // Send to backend API
      const response = await axios.post(`${API}/leads`, payload);

      // Send to webhook if configured
      if (WEBHOOK_URL) {
        try {
          await axios.post(WEBHOOK_URL, payload, {
            headers: { "Content-Type": "application/json" }
          });
        } catch (webhookError) {
          console.error("Webhook error:", webhookError);
          // Don't fail the submission if webhook fails
        }
      }

      // Push to dataLayer for GTM
      if (typeof window !== "undefined" && window.dataLayer) {
        window.dataLayer.push({
          event: "lead_submitted",
          lead_id: response.data.id,
          page_type: pageType,
          service: pageType,
          utm_source: utmParams.utm_source,
          utm_medium: utmParams.utm_medium,
          utm_campaign: utmParams.utm_campaign,
          lead_name: formData.name,
          lead_email: formData.email,
          lead_phone: formData.phone,
        });
      }

      toast.success("Thank you! Redirecting to schedule your consultation...");

      // Redirect to scheduling with lead data
      setTimeout(() => {
        navigate("/scheduling", {
          state: {
            leadId: response.data.id,
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            service: pageType,
          },
        });
      }, 1000);
    } catch (error) {
      console.error("Form submission error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="glass-form p-8 rounded-2xl shadow-2xl border border-gray-100 w-full max-w-md" data-testid="lead-form-container">
      <h3 className="text-2xl font-bold text-gray-900 mb-2 font-['Playfair_Display']" data-testid="lead-form-title">
        {title}
      </h3>
      <p className="text-gray-600 mb-6 text-sm">
        Schedule your free, no-obligation design consultation today.
      </p>

      <form onSubmit={handleSubmit} className="space-y-5" data-testid="lead-form">
        {/* Hidden Fields */}
        <input type="hidden" name="utm_source" value={utmParams.utm_source} />
        <input type="hidden" name="utm_medium" value={utmParams.utm_medium} />
        <input type="hidden" name="utm_campaign" value={utmParams.utm_campaign} />
        <input type="hidden" name="page_type" value={pageType} />
        <input type="hidden" name="service" value={pageType} data-testid="hidden-service-field" />

        <div className="space-y-2">
          <Label htmlFor="name" className="text-sm font-medium text-gray-700">
            Full Name *
          </Label>
          <Input
            id="name"
            name="name"
            type="text"
            required
            placeholder="John Smith"
            value={formData.name}
            onChange={handleChange}
            className="h-12 bg-gray-50 border-gray-200 focus:border-[#1E4C9A] focus:ring-[#1E4C9A] rounded-lg"
            data-testid="lead-form-name"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone" className="text-sm font-medium text-gray-700">
            Phone Number *
          </Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            required
            placeholder="(505) 555-1234"
            value={formData.phone}
            onChange={handleChange}
            className="h-12 bg-gray-50 border-gray-200 focus:border-[#1E4C9A] focus:ring-[#1E4C9A] rounded-lg"
            data-testid="lead-form-phone"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm font-medium text-gray-700">
            Email Address *
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            required
            placeholder="john@example.com"
            value={formData.email}
            onChange={handleChange}
            className="h-12 bg-gray-50 border-gray-200 focus:border-[#1E4C9A] focus:ring-[#1E4C9A] rounded-lg"
            data-testid="lead-form-email"
          />
        </div>

        <Button
          type="submit"
          disabled={isLoading}
          className="w-full h-14 bg-[#1E4C9A] hover:bg-[#153975] text-white font-semibold text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300"
          data-testid="lead-form-submit"
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <CheckCircle className="mr-2 h-5 w-5" />
              Get My Free Quote
            </>
          )}
        </Button>

        <p className="text-xs text-gray-500 text-center mt-4">
          By submitting, you agree to receive calls and texts about your project.
        </p>
      </form>
    </div>
  );
};
